import { GridSize, Grid2 as Grid, TextField } from "@mui/material";
import React, { useState } from "react";
import useDebugInformation from "../../../hooks/useDebugInformation";
import { Prefix } from "@utils/BasicTypes";
import { handleBasicChange } from '../../../utilities/general/index';
import useNato from "../../../hooks/useNato";
import { FormHandlerObject } from "@utils/general/interfaces";
import { StringProcessor } from "@utils/stringProcessor";
import { isEmailValidForReact } from "@utils/validation";
import { useAppSelector } from "@store/hooks";
import { StateKeys, UIKeys } from "@services/storage/storeTemplate";
import { Tooltip } from '@mui/material';

type A11yProps = {
    (name: string, prefix: Prefix, form?: FormHandlerObject): {
        id: string
        name: string
        fullWidth: boolean
        autoComplete: string
        "aria-controls": string
        onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
    }
}

const a11yProps: A11yProps = (name, prefix, form) => {
    if (form && prefix && name && name !== "Country" && !form.list.includes(`${prefix}${name}`))
        form.list.push(`${prefix}${name}`)
    return {
        id: `input - ${name.toLowerCase()} `,
        name,
        fullWidth: true,
        autoComplete: 'none',

        onChange: (event) => { handleBasicChange(event, prefix) },
        "aria-controls": `action - input - ${name.toLowerCase()} `,
    };
}

export type MemoizedFormInputProps = {
    parent: string
    prefix: Prefix
    inputName: "Street" | "City" | "Locality" | "Country" | "FirstName" | "LastName" | "Email" | "Relationship" | "BusinessName" | "SortCode" | "AccountNumber"
    xs: GridSize
    sm: GridSize
    disabled: boolean
    form?: FormHandlerObject
}
const isEmail = (inputName: string) => inputName.toLowerCase() === "email";

const notRequired = ["country", "locality"]

const MemoizedFormInput: React.FC<MemoizedFormInputProps> = ({ parent, prefix, inputName, xs, sm, disabled, form }) => {
    const inputStatePrefix = `${prefix}${inputName}`;
    const monitored = useAppSelector(({ ui }) => ui[inputStatePrefix as UIKeys]);
    const helper = useAppSelector(({ ui }) => ui[`${inputStatePrefix}HelperText` as UIKeys]);
    const state = useAppSelector(({ state }) => state[`${inputStatePrefix}Valid` as StateKeys]);
    const [isValid, setIsValid] = useState(true);
    const nato = useNato(inputName, monitored); // Tooltip content if nato is enabled for this input

    const calculateValidity = React.useCallback((value: string) => {
        if (notRequired.includes(inputName.toLowerCase())) return true;
        if (isEmail(inputName)) {
            if (value.length === 0) return true;
            isEmailValidForReact(value, setIsValid);
        };
        return state;
    }, [inputName, state]);

    React.useEffect(() => {
        setIsValid(calculateValidity(monitored));
        // Perform necessary actions based on the new validity
    }, [calculateValidity, monitored]); // Dependency on the monitored state

    // Memoized values
    const label = React.useMemo(() => StringProcessor.camelToWords(inputName), [inputName]);
    const colour = React.useMemo(() => isValid ? "primary" : "error", [isValid]);
    const helperText = React.useMemo(() => !isValid ? helper : "", [isValid, helper]);

    useDebugInformation(`${parent}▷[${inputName}]`, { parent, inputName, prefix, xs, sm, sanitized: monitored });

    return (
        (<Grid
            size={{
                xs,
                sm
            }}>
            <Tooltip title={nato} sx={{ maxWidth: "none" }} placement="right">
                <TextField
                    disabled={disabled}
                    value={monitored || ""}
                    {...a11yProps(inputName, prefix, form)}
                    label={label}
                    error={!isValid}
                    color={colour}
                    helperText={helperText}
                />
            </Tooltip>
        </Grid>)
    );
};

export default React.memo(MemoizedFormInput)